import React, { useEffect, useState, useCallback } from 'react';
import axios from '../common/axios';
import { Paper, Box, Select, MenuItem } from '@mui/material';
import { useRecoilState } from 'recoil';
import { chartDataState } from '../recoil/atom';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);
dayjs.extend(timezone);

const fetchLevel1Data = async (project) => {
    try {
        const response = await axios.get(`/getlevel1?project=${project}`);
        if (response.data.success) {
            return response.data.level1Data.filter(v => v.value !== null).map(v => v.value);
        }
    } catch (error) {
        console.error('Error fetching level1 data:', error);
        // TODO: Provide appropriate feedback to the user
    }
    return [];
};

const fetchFilterData = async (project, level1) => {
    try {
        const response = await axios.get(`/getfilter?project=${project}&level1=${level1}`);
        if (response.data.success) {
            return response.data.data;
        }
    } catch (error) {
        console.error('Error fetching filter data:', error);
        // TODO: Provide appropriate feedback to the user
    }
    return {};
};

const fetchSelectedData = async (project, level1, level2, level3, cutoffdate) => {
    try {
        const response = await axios.get(`/getselectedData?project=${project}&level1=${level1}&level2=${level2}&level3=${level3}&cutoffdate=${cutoffdate}`);
        if (response.data.success && level3.includes("전체 중공종")) {
            return response.data.data;
        } else if (response.data.success && !level3.includes("전체 중공종")){
            return response.data.data.map((v)=>({...v, unit:v.unit2, actualQTY:v.actualQTY2, planQTY:v.planQTY2}));
        }
    } catch (error) {
        console.error('Error fetching selected data:', error);
        // TODO: Provide appropriate feedback to the user
    }
    return null;
};

const selectStyles = {
    height: '100%',
    '& .MuiSelect-select': {
        paddingY: 0,
        fontSize: 18,
        fontWeight: 'bold',
        color: '#262626',
        display: 'flex',
        alignItems: 'center',
        height: '100% !important',
        lineHeight: '1',
    },
    '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
};

const HeaderTitle = ({ selectedProject, criteriaDate }) => {
    const [level1, setLevel1] = useState([]);
    const [level2, setLevel2] = useState(["전체 협력업체"]);
    const [level3, setLevel3] = useState(["전체 중공종"]);
    const [selectedLevel1, setSelectedLevel1] = useState(null);
    const [selectedLevel2, setSelectedLevel2] = useState("전체 협력업체");
    const [selectedLevel3, setSelectedLevel3] = useState("전체 중공종");

    const [chartData, setChartData] = useRecoilState(chartDataState);

    const handleLevel1Change = useCallback((event) => {
        setSelectedLevel1(event.target.value);
    }, []);

    const handleLevel2Change = useCallback((event) => {
        setSelectedLevel2(event.target.value);
    }, []);

    const handleLevel3Change = useCallback((event) => {
        setSelectedLevel3(event.target.value);
    }, []);

    useEffect(() => {
        if (selectedProject) {
            fetchLevel1Data(selectedProject).then(data => {
                setLevel1(data);
                setSelectedLevel1(data.includes("건축") ? "건축" : data[0]);
            });
        }
    }, [selectedProject]);

    useEffect(() => {
        if (selectedProject && selectedLevel1) {
            fetchFilterData(selectedProject, selectedLevel1).then(data => {
                setLevel2(["전체 협력업체", ...Object.keys(data)]);
                setSelectedLevel2("전체 협력업체");
            });
        }
    }, [selectedProject, selectedLevel1]);

    useEffect(() => {
        if (selectedProject && selectedLevel2) {
            fetchFilterData(selectedProject, selectedLevel1).then(data => {
                if(data[selectedLevel2]?.length>0){
                setLevel3(["전체 중공종", ...data[selectedLevel2]]);}
                setSelectedLevel3("전체 중공종");
            });
        }
    }, [selectedProject, selectedLevel1, selectedLevel2]);

    useEffect(() => {
        if (selectedProject && selectedLevel1 && selectedLevel2 && selectedLevel3 && criteriaDate) {
            const formattedDate = dayjs(criteriaDate)
                .tz('Asia/Seoul')
                .format('YYYY-MM-DD');
            
            fetchSelectedData(selectedProject, selectedLevel1, selectedLevel2, selectedLevel3, formattedDate).then(data => {
                setChartData(data);
            });
        }
    }, [selectedProject, selectedLevel1, selectedLevel2, selectedLevel3, criteriaDate, setChartData]);

    return (
        <Paper
            elevation={3}
            sx={{
                borderRadius: "4px",
                border: "1px solid #EFF0F6",
                boxShadow: "0px 5px 20px 0px rgba(0, 0, 0, 0.05)",
                height: '100%',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                transition: 'all 0.3s ease-in-out',
                padding: '16px',
                boxSizing: 'border-box',
                position: 'relative',
            }}
        >
            <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                height: '100%',
                width: '100%',
                justifyContent: 'center'
            }}>
                <Select
                    value={selectedLevel1 || ''}
                    onChange={handleLevel1Change}
                    sx={selectStyles}
                    IconComponent={() => null}
                >
                    {level1.map((level) => (
                        <MenuItem key={level} value={level}>
                            {level}
                        </MenuItem>
                    ))}
                </Select>
    
                <Box sx={{ mx: 1, color: '#262626', fontSize: 18 }}>/</Box>
    
                <Select
                    value={selectedLevel2 || ''}
                    onChange={handleLevel2Change}
                    sx={selectStyles}
                    IconComponent={() => null}
                >
                    {level2.map((level) => (
                        <MenuItem key={level} value={level}>
                            {level}
                        </MenuItem>
                    ))}
                </Select>
    
                <Box sx={{ mx: 1, color: '#262626', fontSize: 18 }}>/</Box>
    
                <Select
                    value={selectedLevel3 || ''}
                    onChange={handleLevel3Change}
                    sx={selectStyles}
                    IconComponent={() => null}
                >
                    {level3.map((level) => (
                        <MenuItem key={level} value={level}>
                            {level}
                        </MenuItem>
                    ))}
                </Select>
            </Box>
        </Paper>
    );
};

export default HeaderTitle;